import React, { useMemo } from "react";
import Input from "../../../../Components/Common/Input/Input";
import { BudgetRow } from "../types/precon.types";
import { CHAR_LIMIT } from "../../../../utils/Common/Constant";
import Table, { Column } from "Components/Table/Table";

const numericOnly = (val: string) => val.replace(/[^0-9$,.]/g, "");

const parseNum = (val: string | undefined) =>
  parseFloat((val ?? "").replace(/[$,]/g, "")) || 0;

const numericDecimal = (val: string) => {
  const cleaned = val.replace(/[^0-9.]/g, "");
  const parts = cleaned.split(".");
  if (parts.length === 1) return parts[0];
  return parts[0] + "." + parts[1].slice(0, 2);
};

interface BudgetTableProps {
  rows: BudgetRow[];
  onRowChange: (index: number, field: keyof BudgetRow, value: string) => void;
}
const BudgetTable = ({ rows, onRowChange }: BudgetTableProps) => {
  const totals = useMemo(() => {
    const dataRows = rows.filter((r) => !r.isTotal);
    return {
      value: dataRows.reduce((sum, r) => sum + parseNum(r.value), 0),
      projectSf: dataRows.reduce((sum, r) => sum + parseNum(r.projectSf), 0),
      projectPsf: dataRows.reduce((sum, r) => sum + parseNum(r.projectPsf), 0),
      industryLow: dataRows.reduce(
        (sum, r) => sum + parseNum(r.industryLow),
        0,
      ),
      industryHigh: dataRows.reduce(
        (sum, r) => sum + parseNum(r.industryHigh),
        0,
      ),
    };
  }, [rows]);

  return (
    <div>
      <div className="tableScroll">
        <Table
          tableClassName="budgetTable"
          data={rows}
          rowClassName={(row) => (row.isTotal ? "totalRow" : "")}
          columns={
            [
              {
                header: "Trade / Scope",
                render: (row, i) =>
                  row.isTotal ? (
                    row.trade ?? "Total"
                  ) : (
                    <Input
                      className="left"
                      value={row.trade}
                      maxLength={CHAR_LIMIT.SHORT}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        onRowChange(i, "trade", e.target.value)
                      }
                    />
                  ),
              },
              {
                header: "Scheduled Value",
                render: (row, i) =>
                  row.isTotal ? (
                    <><span className="inputAddon">$</span>{String(totals.value)}</>
                  ) : (
                    <Input
                      value={row.value}
                      maxLength={CHAR_LIMIT.SHORT}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        onRowChange(i, "value", numericOnly(e.target.value))
                      }
                    />
                  ),
              },
              {
                header: "Project SF",
                render: (row) =>
                  row.isTotal ? (
                    String(totals.projectSf)
                  ) : (
                    <Input value={row.projectSf} className="autoFill" readOnly placeholder="auto" />
                  ),
              },
              {
                header: "Project $/SF",
                render: (row) =>
                  row.isTotal ? (
                    <><span className="inputAddon">$</span>{totals.projectPsf.toFixed(2)}</>
                  ) : (
                    <div className="inputAddonWrap">
                      <span className="inputAddon">$</span>
                      <Input value={row.projectPsf} className="autoFill" readOnly placeholder="auto" />
                    </div>
                  ),
              },
              {
                header: "Industry Low",
                render: (row, i) =>
                  row.isTotal ? (
                    <><span className="inputAddon">$</span>{totals.industryLow.toFixed(2)}</>
                  ) : (
                    <div className="inputAddonWrap">
                      <span className="inputAddon">$</span>
                      <Input
                        style={{ paddingLeft: "3px" }}
                        value={row.industryLow}
                        placeholder="0.00"
                        maxLength={CHAR_LIMIT.SHORT}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          onRowChange(i, "industryLow", numericDecimal(e.target.value))
                        }
                      />
                    </div>
                  ),
              },
              {
                header: "Industry High",
                render: (row, i) =>
                  row.isTotal ? (
                    <><span className="inputAddon">$</span>{totals.industryHigh.toFixed(2)}</>
                  ) : (
                    <div className="inputAddonWrap">
                      <span className="inputAddon">$</span>
                      <Input
                        style={{ paddingLeft: "3px" }}
                        value={row.industryHigh}
                        placeholder="0.00"
                        maxLength={CHAR_LIMIT.SHORT}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          onRowChange(i, "industryHigh", numericDecimal(e.target.value))
                        }
                      />
                    </div>
                  ),
              },
              {
                header: "Notes",
                render: (row, i) =>
                  row.isTotal ? (
                    row.notes
                  ) : (
                    <Input
                      className="left"
                      value={row.notes}
                      placeholder="Note"
                      maxLength={CHAR_LIMIT.SHORT}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        onRowChange(i, "notes", e.target.value)
                      }
                    />
                  ),
              },
            ] as Column<BudgetRow>[]
          }
        />
      </div>
    </div>
  );
};

export default BudgetTable;
